import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const DRAFT_PREFIX = 'draft:';
const RESTORED_KEYS = new Set();  // dedupe toasts across re-renders / re-mounts

/**
 * Persist a controlled-input value to localStorage as the user types so they
 * never lose work to a session timeout, accidental refresh, or browser crash.
 *
 * Usage:
 *   const [note, setNote] = useState('');
 *   const { clearDraft } = useFieldDraft('personnel-note:' + personnelId, note, setNote);
 *   // ... after the server confirms the save:
 *   clearDraft();
 *
 * Behavior:
 *   - On mount: if a draft is stored for `key`, restores it via setValue()
 *     AND shows a one-shot "Draft restored" toast.
 *     Only restores if the current `value` is empty/whitespace — never
 *     overwrites a value that was already loaded from server-side state.
 *   - On change: debounced (700ms) localStorage write under `draft:<key>`.
 *   - clearDraft(): removes the stored draft (call after a successful Save).
 */
export function useFieldDraft(key, value, setValue, { debounceMs = 700, toastOnRestore = true } = {}) {
  const restoredRef = useRef(false);
  const lastWrittenRef = useRef(null);
  const timerRef = useRef(null);

  // Restore once on mount (or when key changes).
  useEffect(() => {
    if (!key) return;
    if (restoredRef.current) return;
    try {
      const stored = localStorage.getItem(DRAFT_PREFIX + key);
      const isEmpty = !value || (typeof value === 'string' && value.trim() === '');
      if (stored && isEmpty) {
        setValue(stored);
        if (toastOnRestore && !RESTORED_KEYS.has(key)) {
          RESTORED_KEYS.add(key);
          // Defer to avoid toasting before the page is rendered.
          setTimeout(() => {
            toast.info('Draft restored — picked up where you left off');
          }, 250);
        }
      }
    } catch (e) { /* ignore */ }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Debounced write whenever value changes.
  useEffect(() => {
    if (!key) return;
    if (!restoredRef.current) return; // skip the very first run before restore
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        const s = typeof value === 'string' ? value : '';
        if (s.trim() === '') {
          if (lastWrittenRef.current !== null) {
            localStorage.removeItem(DRAFT_PREFIX + key);
            lastWrittenRef.current = null;
          }
          return;
        }
        if (s !== lastWrittenRef.current) {
          localStorage.setItem(DRAFT_PREFIX + key, s);
          lastWrittenRef.current = s;
        }
      } catch (e) { /* localStorage full or blocked — silently skip */ }
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [key, value, debounceMs]);

  const clearDraft = () => {
    try {
      if (key) localStorage.removeItem(DRAFT_PREFIX + key);
      lastWrittenRef.current = null;
      RESTORED_KEYS.delete(key);
    } catch (e) { /* ignore */ }
  };

  return { clearDraft };
}
